import { useTranslation } from 'react-i18next';
import { PLATFORM_CATEGORY_LABELS } from '@/entities/media-connector';
import { BreadcrumbPageHeader, NavigationPanel } from '@/shared/ui/layout';
import { ROUTES } from '@/shared/config/routes';
import { MediaConnectorPane } from './MediaConnectorPane';
import { DataSourcePane, isDataSourceId } from './DataSourcePane';
import { StatistaPlaceholderPane } from './StatistaPlaceholderPane';
import { useMediaConnectorsWorkspace } from './hooks/useMediaConnectorsWorkspace';
import type { CategoryFilter } from './hooks/useMediaConnectorsWorkspace.types';

/**
 * Shell for the platforms `NavigationPanel` and the pane of the platform it
 * opens. The rail's state (platform list, category filter) lives here, above the
 * pane, which is remounted by `key` so its own search/filter/dialog state
 * resets per platform switch instead of needing a reset effect.
 *
 * The pane's own search (via `MediaConnectorToolbar`) searches a different
 * thing — accounts within the one active platform — and is untouched here.
 *
 * The rail also carries the IQ file-upload data sources, which share the `:platform` param
 * but have no accounts, so they get their own pane.
 */
export function MediaConnectorsWorkspace() {
  const { t } = useTranslation('knowledge');
  const {
    items,
    isLoadingPlatforms,
    activePlatform,
    categoryFilter,
    setCategoryFilter,
    categoryFilters,
    hrefFor,
    showStatistaPane,
    isStatistaConnected,
    setIsStatistaConnected,
  } = useMediaConnectorsWorkspace();

  return (
    <div data-testid="media-connectors-workspace" className="flex h-full min-h-0 flex-col gap-xs">
      <BreadcrumbPageHeader
        trail={[
          {
            label: t('mediaConnectors.mediaConnectorsHeader.knowledge'),
            href: ROUTES.KNOWLEDGE_HUB,
          },
        ]}
        page={t('mediaConnectors.mediaConnectorsHeader.paidMediaAdIntelligence')}
      />
      <div data-testid="media-connectors-workspace-panels" className="flex min-h-0 flex-1 gap-xs">
        <NavigationPanel
          data-testid="media-platforms-panel"
          title={t('mediaConnectors.mediaConnectorsHeader.platforms')}
          items={items}
          activeItemId={activePlatform}
          isLoading={isLoadingPlatforms}
          emptyMessage={t('mediaConnectors.mediaConnectors.noPlatformsInThisCategory')}
          hrefFor={hrefFor}
          filters={{
            label: t('mediaConnectors.mediaConnectors.filterPlatforms'),
            groups: [
              {
                label: t('mediaConnectors.mediaConnectors.filterByCategory'),
                value: categoryFilter,
                // Offered as the hook hands them over: a category whose
                // platforms are all flag-disabled never reaches here, because
                // picking it would filter the panel to nothing.
                options: categoryFilters.map((category) => ({
                  id: category,
                  label:
                    category === 'all'
                      ? t('mediaConnectors.mediaConnectors.all')
                      : PLATFORM_CATEGORY_LABELS[category],
                })),
                onChange: (id) => setCategoryFilter(id as CategoryFilter),
              },
            ],
          }}
        />
        {showStatistaPane ? (
          <StatistaPlaceholderPane
            connected={isStatistaConnected}
            onConnectedChange={setIsStatistaConnected}
          />
        ) : isDataSourceId(activePlatform) ? (
          <DataSourcePane key={activePlatform} source={activePlatform} />
        ) : (
          <MediaConnectorPane key={activePlatform ?? 'none'} platform={activePlatform} />
        )}
      </div>
    </div>
  );
}

MediaConnectorsWorkspace.displayName = 'MediaConnectorsWorkspace';
